import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

// Admin-only "Download PDF" button in the dark hero of Article / How Does It Work? pages.
// Renders nothing for guests and non-admin users; the backend PDF endpoints are
// admin-only as well (GET /api/articles/static/{slug}/pdf, /api/how-it-works/{id}/pdf),
// so hiding the button is a convenience, not the gate. `onDownload` returns a Promise of
// the PDF Blob; the file is saved as "AUTOXIUM-<title>.pdf".
const AdminPdfDownloadButton = ({ onDownload, title }) => {
  const { isAdmin } = useAuth();
  const [downloading, setDownloading] = useState(false);

  if (!isAdmin || !onDownload) return null;

  const handleClick = async () => {
    setDownloading(true);
    try {
      const blob = await onDownload();
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const safeTitle = (title || 'document').replace(/[^\w\s()-]/g, '').trim().replace(/\s+/g, '-');
      const link = document.createElement('a');
      link.href = url;
      link.download = `AUTOXIUM-${safeTitle}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      toast.error('Could not generate the PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={downloading}
      title="Download PDF (admin only)"
      className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-white/10 hover:bg-white/20 text-white/90 text-sm font-mono uppercase tracking-wider transition-colors disabled:opacity-60"
      data-testid="admin-download-pdf"
    >
      {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      {downloading ? 'Preparing…' : 'PDF'}
    </button>
  );
};

export default AdminPdfDownloadButton;
